import React, { useState, useEffect } from 'react';
import api from '../../api';
import './ReferralSystem.css';

const ReferralSystem = () => {
  document.title = "Referral Shop | Pokémon Radiance";

  const [referralItems, setReferralItems] = useState([]);
  const [userReferralPoints, setUserReferralPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [purchasingItemId, setPurchasingItemId] = useState(null);
  const [purchaseMessage, setPurchaseMessage] = useState("");
  const [purchaseError, setPurchaseError] = useState("");
  const [itemQuantities, setItemQuantities] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  // Referral info state
  const [referralInfo, setReferralInfo] = useState(null);
  const [referrerUsername, setReferrerUsername] = useState("");
  const [submittingReferral, setSubmittingReferral] = useState(false);
  const [referralMessage, setReferralMessage] = useState("");

  useEffect(() => {
    loadReferralData();
  }, []);

  const loadReferralData = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get("/v1/referral-shop", { withCredentials: true }),
      api.get("/v1/referral-info", { withCredentials: true })
    ])
      .then(([shopRes, infoRes]) => {
        if (shopRes.data.success) {
          setReferralItems(shopRes.data.items);
          setUserReferralPoints(shopRes.data.referral_points);
        }
        if (infoRes.data.success) {
          setReferralInfo(infoRes.data);
        }
      })
      .catch(() => setError("Failed to load referral data"))
      .finally(() => setLoading(false));
  };

  const handleQuantityChange = (itemId, value) => {
    const quantity = Math.max(1, Math.min(10, parseInt(value) || 1));
    setItemQuantities(prev => ({
      ...prev,
      [itemId]: quantity
    }));
  };

  const handlePurchase = (itemId, cost) => {
    if (userReferralPoints < cost * (itemQuantities[itemId] || 1) || purchasing) return;
    
    setPurchasing(true);
    setPurchasingItemId(itemId);
    setPurchaseError("");
    setPurchaseMessage("");
    
    const quantity = itemQuantities[itemId] || 1;
    const totalCost = cost * quantity;
    
    api.post('/v1/purchase-referral-item', { shop_item_id: itemId, quantity }, { withCredentials: true })
      .then(res => {
        if (res.data.success) {
          setUserReferralPoints(prev => prev - totalCost);
          const itemName = res.data.item_name || "item";
          const itemQuantity = res.data.quantity || 1;
          setPurchaseMessage(`Successfully purchased ${itemQuantity > 1 ? `${itemQuantity}× ` : ''}${itemName}!`);
          // Refresh data to get updated points
          setTimeout(() => {
            loadReferralData();
          }, 1000);
        }
      })
      .catch(err => {
        const msg = err.response?.data?.detail || 'Purchase failed';
        setPurchaseError(msg);
      })
      .finally(() => {
        setPurchasing(false);
        setPurchasingItemId(null);
        setTimeout(() => {
          setPurchaseMessage("");
          setPurchaseError("");
        }, 3000);
      });
  };

  const handleSubmitReferral = () => {
    if (!referrerUsername.trim() || submittingReferral) return;
    
    setSubmittingReferral(true);
    setReferralMessage("");
    
    api.post('/v1/submit-referral', { referrer_username: referrerUsername.trim() }, { withCredentials: true })
      .then(res => {
        if (res.data.success) {
          setReferralMessage("Referrer submitted successfully!");
          setReferrerUsername("");
          // Refresh referral info
          setTimeout(() => {
            loadReferralData();
          }, 1000);
        }
      })
      .catch(err => {
        const msg = err.response?.data?.detail || 'Failed to submit referrer';
        setReferralMessage(msg);
      })
      .finally(() => {
        setSubmittingReferral(false);
        setTimeout(() => {
          setReferralMessage("");
        }, 3000);
      });
  };

  if (loading) {
    return (
      <div className="page-content referral-loading">
        <div className="loading-spinner"></div>
        <div>Loading Referral System...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content referral-error">
        <div className="error-message">{error}</div>
        <button onClick={loadReferralData} className="retry-button">Retry</button>
      </div>
    );
  }

  return (
    <div className="referral-shop-page">
      <div className="referral-shop-header">
       
       
      </div>
      {/* How to Earn Section */}
      <div className="how-to-earn-section">
        <div className="section-title">How to Earn Points</div>
        <div className="earn-methods">
          <div className="earn-method">
            <div className="earn-description">Step 1 - Refer a friend to join the game</div>
          </div>
          <div className="earn-method">
            <div className="earn-description">Step 2 - They create an account and link their Discord account (+5 points)</div>
          </div>
        </div>
      </div>
      
      {/* Main Shop Content */}
      <div className="referral-shop-content">
        <div className="shop-section-title">Available Rewards</div>
        
        <div className="shop-description">
          Spend your hard-earned referral points on these exclusive rewards!
        </div>
        <div className="currency-display">
          <div className="currency-item">
            <img src="/items/point-coin.png" alt="Points" className="currency-icon" />
            {userReferralPoints} Points
          </div>
        </div>

        {/* Purchase Messages */}
        {purchaseMessage && (
          <div className="game-notification success">
            <div className="notification-icon">✓</div>
            <div className="notification-text">{purchaseMessage}</div>
          </div>
        )}
        {purchaseError && (
          <div className="game-notification error">
            <div className="notification-icon">✕</div>
            <div className="notification-text">{purchaseError}</div>
          </div>
        )}

        {/* Items Grid with Pagination */}
        <div className="referral-items-grid">
          {referralItems
            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
            .map((item) => (
            <div key={item.id} className="game-item-card">
              <div className="item-shine"></div>
              <div className="item-header">
                <div className="item-name">{item.name}</div>
                <div className="item-amount">×{item.amount}</div>
              </div>
              
              <div className="item-image-container">
                {item.item_image ? (
                  <img 
                    src={`/${item.item_image}`} 
                    alt={item.name} 
                    className="item-image" 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/items/unknown.png';
                    }}
                  />
                ) : (
                  <div className="item-image-placeholder">🎁</div>
                )}
              </div>
              
              {item.description && (
                <div className="item-description">{item.description}</div>
              )}
              
              <div className="item-cost">
                <span className="cost-label">Cost:</span>
                <span className="cost-value">
                  <img src="items/point-coin.png" alt="Points" className="points-icon-small" /> {item.referral_cost} 
                </span>
              </div>
              <div className="total-cost">
                {(itemQuantities[item.id] || 1) > 1 && (
                  <span className="cost-multiplier">
                    Total: <img src="items/point-coin.png" alt="Points" className="points-icon-small" /> 
                    {item.referral_cost * (itemQuantities[item.id] || 1)}
                  </span>
                )}
              </div>
              <button
                onClick={() => handlePurchase(item.id, item.referral_cost)}
                disabled={userReferralPoints < (item.referral_cost * (itemQuantities[item.id] || 1)) || purchasing}
                className={`game-button ${purchasing && purchasingItemId === item.id ? 'purchasing' : ''}`}
              >
                {purchasing && purchasingItemId === item.id ? 'Purchasing...' : `Purchase${(itemQuantities[item.id] || 1) > 1 ? ` (${itemQuantities[item.id]}×)` : ''}`}
              </button>
            </div>
          ))}
        </div>

        {referralItems.length === 0 && (
          <div className="no-items-container">
            <div className="no-items-icon">📦</div>
            <div className="no-items-text">No referral items available</div>
          </div>
        )}
        </div>
        
        {/* Pagination Controls */}
        {referralItems.length > itemsPerPage && (
          <div className="pagination-controls">
            <button 
              className="pagination-button" 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              &lt; Prev
            </button>
            <div className="pagination-info">
              Page {currentPage} of {Math.ceil(referralItems.length / itemsPerPage)}
            </div>
            <button 
              className="pagination-button" 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(referralItems.length / itemsPerPage)))}
              disabled={currentPage === Math.ceil(referralItems.length / itemsPerPage)}
            >
              Next &gt;
            </button>
          </div>
        )}
      </div>
 
  );
};

export default ReferralSystem;
